/**
 * Tokens do design system em TypeScript, para quem não lê CSS (GSAP, Recharts, canvas).
 * Mesmos valores de src/styles/theme.css; o teste tokens.test.ts falha se divergirem.
 * Em componentes React, prefira as classes do Tailwind ou `cssVar()`, que seguem o tema ativo.
 */

export const COLOR_TOKENS = [
  'canvas',
  'surface',
  'surface-2',
  'border',
  'ink',
  'ink-2',
  'ink-3',
  'petroleo',
  'petroleo-soft',
  'jade',
  'ambar',
  'framboesa',
  'ardosia',
  'on-petroleo',
  'on-framboesa',
] as const
export type ColorToken = (typeof COLOR_TOKENS)[number]

export type ThemeName = 'light' | 'dark'

export const colors: Record<ThemeName, Record<ColorToken, string>> = {
  light: {
    canvas: '#f3f5f4',
    surface: '#ffffff',
    'surface-2': '#e9edec',
    border: '#d9e0de',
    ink: '#1b2b34',
    'ink-2': '#55676e',
    'ink-3': '#8a9a9f',
    petroleo: '#00745a',
    'petroleo-soft': '#dcf3ec',
    jade: '#1f8a5b',
    ambar: '#d08a1f',
    framboesa: '#c62f5b',
    ardosia: '#64748b',
    'on-petroleo': '#ffffff',
    'on-framboesa': '#ffffff',
  },
  dark: {
    canvas: '#0e1517',
    surface: '#141e21',
    'surface-2': '#1b272b',
    border: '#243236',
    ink: '#e8eeec',
    'ink-2': '#9db0b2',
    'ink-3': '#6c7f83',
    petroleo: '#2ec4a0',
    'petroleo-soft': '#12382f',
    jade: '#4cc38a',
    ambar: '#e8a93a',
    framboesa: '#f0668c',
    ardosia: '#8e9bae',
    'on-petroleo': '#0e1517',
    'on-framboesa': '#0e1517',
  },
}

/** Cores semânticas: cada status, variação e série de gráfico usa exatamente uma delas. */
export const SEMANTIC_TONES = ['petroleo', 'jade', 'ambar', 'framboesa', 'ardosia'] as const
export type SemanticTone = (typeof SEMANTIC_TONES)[number]

/** Referência CSS ao token, que acompanha o tema ativo (`var(--color-jade)`). */
export function cssVar(token: ColorToken): string {
  return `var(--color-${token})`
}

/** Valor resolvido do token no tema pedido (para canvas, PDFs ou bibliotecas sem CSS vars). */
export function tokenColor(token: ColorToken, theme: ThemeName = 'light'): string {
  return colors[theme][token]
}

/**
 * Paleta dos gráficos (Sprint 8), validada com o skill dataviz nos dois temas
 * (docs/decisions.md, 17/10/2026). Mesmos valores de `--chart-*` em theme.css.
 * Categórica em ordem fixa, atribuída em sequência e nunca reciclada: a ordem
 * petróleo, âmbar, jade, ardósia, framboesa é a que passa no piso de distinção entre vizinhos.
 * Sequencial de petróleo para magnitude (mapa de calor): seq-1 é o menor valor.
 */
export const CHART_CATEGORICAL = ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5'] as const
export const CHART_SEQUENTIAL = [
  'chart-seq-1',
  'chart-seq-2',
  'chart-seq-3',
  'chart-seq-4',
  'chart-seq-5',
] as const
export type ChartToken = (typeof CHART_CATEGORICAL)[number] | (typeof CHART_SEQUENTIAL)[number]

/** Matiz de cada série categórica, para quem escolhe a cor pelo significado. */
export const chartHue: Record<SemanticTone, (typeof CHART_CATEGORICAL)[number]> = {
  petroleo: 'chart-1',
  ambar: 'chart-2',
  jade: 'chart-3',
  ardosia: 'chart-4',
  framboesa: 'chart-5',
}

export const chartColors: Record<ThemeName, Record<ChartToken, string>> = {
  light: {
    'chart-1': '#1d9999',
    'chart-2': '#c78109',
    'chart-3': '#2b885c',
    'chart-4': '#4e75af',
    'chart-5': '#c9305d',
    'chart-seq-1': '#47c7c6',
    'chart-seq-2': '#1baaaa',
    'chart-seq-3': '#127f7f',
    'chart-seq-4': '#0b6262',
    'chart-seq-5': '#064545',
  },
  dark: {
    'chart-1': '#0fa49e',
    'chart-2': '#bb8317',
    'chart-3': '#21a870',
    'chart-4': '#6490d2',
    'chart-5': '#e05a80',
    'chart-seq-1': '#055959',
    'chart-seq-2': '#0f7070',
    'chart-seq-3': '#06999a',
    'chart-seq-4': '#36baba',
    'chart-seq-5': '#5edada',
  },
}

/** Referência CSS ao token de gráfico, que acompanha o tema ativo (`var(--chart-1)`). */
export function chartVar(token: ChartToken): string {
  return `var(--${token})`
}

/**
 * Valor resolvido do token de gráfico no elemento (Recharts precisa de cor concreta em alguns
 * pontos, como a exportação PNG). Sem DOM, cai no valor do tema pedido.
 */
export function readChartColor(
  token: ChartToken,
  el?: Element | null,
  fallbackTheme: ThemeName = 'light',
): string {
  if (typeof window !== 'undefined' && typeof getComputedStyle === 'function') {
    const target = el ?? document.documentElement
    const value = getComputedStyle(target).getPropertyValue(`--${token}`).trim()
    if (value) return value
  }
  return chartColors[fallbackTheme][token]
}

/**
 * Paleta de identidade (Sprint 13): 8 tons para a cor da cobradora e de etiquetas, escolhidos no
 * `ColorSwatchPicker`. Os 5 primeiros são os tons semânticos; anil, ameixa e musgo existem só
 * aqui (variáveis `--swatch-*`) e nunca indicam status. A chave é o que se grava no banco.
 */
export const SWATCH_TONES = [
  'petroleo',
  'jade',
  'ambar',
  'framboesa',
  'ardosia',
  'anil',
  'ameixa',
  'musgo',
] as const
export type SwatchTone = (typeof SWATCH_TONES)[number]

/** Nome de cada tom, como aparece na tela e no nome acessível. */
export const SWATCH_LABELS: Record<SwatchTone, string> = {
  petroleo: 'Petróleo',
  jade: 'Jade',
  ambar: 'Âmbar',
  framboesa: 'Framboesa',
  ardosia: 'Ardósia',
  anil: 'Anil',
  ameixa: 'Ameixa',
  musgo: 'Musgo',
}

type ExtraSwatch = Exclude<SwatchTone, SemanticTone>

export const swatchColors: Record<ThemeName, Record<ExtraSwatch, string>> = {
  light: { anil: '#4e75af', ameixa: '#8a4f9e', musgo: '#5f7a26' },
  dark: { anil: '#6490d2', ameixa: '#c08ad3', musgo: '#a3bd5c' },
}

export function isSwatchTone(value: unknown): value is SwatchTone {
  return typeof value === 'string' && (SWATCH_TONES as readonly string[]).includes(value)
}

/** Referência CSS ao tom, que acompanha o tema ativo (`var(--color-jade)`, `var(--swatch-anil)`). */
export function swatchVar(tone: SwatchTone): string {
  return (SEMANTIC_TONES as readonly string[]).includes(tone)
    ? `var(--color-${tone})`
    : `var(--swatch-${tone})`
}

/** Gradientes: só na tela de login e na faixa de aging (design-system §2). */
export const gradients = {
  login: { from: '#0d5560', to: '#00977a' },
  aging: ['jade', 'ambar', 'framboesa'] as const satisfies readonly SemanticTone[],
} as const

export const typography = {
  family: {
    sans: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    condensed: "'IBM Plex Sans Condensed', 'IBM Plex Sans', ui-sans-serif, sans-serif",
  },
  /** px; `weight` e `tracking` só quando diferem do corpo. */
  scale: {
    micro: { size: 12, lineHeight: 16 },
    compact: { size: 13, lineHeight: 18 },
    body: { size: 14, lineHeight: 20 },
    'body-lg': { size: 16, lineHeight: 24 },
    section: { size: 20, lineHeight: 28, weight: 600 },
    page: { size: 24, lineHeight: 32, weight: 600, tracking: '-0.01em' },
    kpi: { size: 32, lineHeight: 40, weight: 600, tracking: '-0.02em' },
    display: { size: 44, lineHeight: 48, weight: 600, tracking: '-0.025em' },
  },
  maxLineLength: '72ch',
} as const

/** Escala de espaçamento em px (densa, pensada para dados). */
export const spacing = [2, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 64] as const

/** Raios em px: chips 6, botões e inputs 8, cards 12, sheets e modais 16, pílulas 999. */
export const radii = { sm: 6, md: 8, lg: 12, xl: 16, full: 999 } as const

/** Sombra só em flutuantes. */
export const shadows = {
  float: {
    light: '0 8px 24px -12px rgb(27 43 52 / 0.25)',
    dark: '0 8px 24px -12px rgb(0 0 0 / 0.6)',
  },
} as const

/** Motion: durações em ms (CSS) e em s (GSAP), easings em nomes GSAP e equivalentes CSS. */
export const motion = {
  duration: { fast: 120, base: 200, slow: 320, hero: 600 },
  ease: { entrance: 'power2.out', state: 'power1.inOut', confirm: 'back.out(1.4)' },
  cssEase: {
    entrance: 'cubic-bezier(0.33, 1, 0.68, 1)',
    state: 'cubic-bezier(0.45, 0, 0.55, 1)',
    confirm: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
  },
  /** Listas: 30 ms entre itens, só nos 12 primeiros. */
  stagger: 30,
  staggerLimit: 12,
  /** Com prefers-reduced-motion: só opacidade, em 100 ms. */
  reducedDuration: 100,
  /** Deslocamento de entrada (login: 12 px; toasts: 8 px). */
  offset: { hero: 12, toast: 8, list: 8 },
} as const

export type MotionDuration = keyof typeof motion.duration

/** Duração em segundos para o GSAP. */
export function seconds(duration: MotionDuration): number {
  return motion.duration[duration] / 1000
}

export const layout = {
  sidebarWidth: 248,
  sidebarCollapsedWidth: 64,
  topbarHeight: 56,
  contentPadding: 24,
  rowHeight: { comfortable: 40, compact: 34 },
  login: { panelRatio: 0.58, formWidth: 400, breakpoint: 960, bandHeight: 160 },
  controlMinHeight: 32,
  iconTargetMin: 24,
  /**
   * Shell responsivo (px): abaixo de `drawer` a sidebar vira gaveta sobreposta; abaixo de
   * `sidebar` ela começa recolhida. Mesmos valores de `--breakpoint-drawer` e `--breakpoint-sidebar`.
   */
  breakpoints: { drawer: 900, sidebar: 1280 },
} as const

export type Density = keyof typeof layout.rowHeight
export type ThemePreference = ThemeName | 'system'
